/**
 * AdminMotivosCierre · /admin/motivos-cierre · v101.101.0
 * Catálogo de motivos de cierre que el técnico elige al cerrar la OT (app) y la supervisión al validar (web).
 *  · Nombre, «exige descripción», actividades a las que aplica (vacío = todas), orden.
 *  · Se DESACTIVAN (no se borran): las OTs ya cerradas conservan el motivo con su nombre.
 * v101.188.0 · Pestaña «Motivos de cancelación»: los elige quien cancela la OT en la web (además del comentario).
 */
import { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import api, { extractApiError } from "../lib/api";
import { toast } from "sonner";
import { ClipboardList, Plus, Loader2, Save, X, Edit3 } from "lucide-react";

const EMPTY = { label: "", requires_description: true, activity_codes: [], active: true, sort_order: 100 };
const KINDS = {
  cierre: {
    tab: "Motivos de cierre",
    help: "El técnico elige uno o varios al cerrar la OT y escribe la descripción. Si una actividad tiene motivos activos, el cierre los exige.",
    empty: "Aún no hay motivos de cierre. Mientras no haya, el cierre no pide motivo.",
    placeholder: "Ej.: Servicio restablecido",
  },
  cancelacion: {
    tab: "Motivos de cancelación",
    help: "Quien cancela la OT en la web elige uno o varios motivos además del comentario. Si una actividad tiene motivos activos, la cancelación los exige y el motivo viaja a Odoo en la nota.",
    empty: "Aún no hay motivos de cancelación. Mientras no haya, la cancelación solo pide el comentario.",
    placeholder: "Ej.: Cliente desiste del servicio",
  },
};

export default function AdminMotivosCierre() {
  const [kind, setKind] = useState(() => (new URLSearchParams(window.location.search).get("tipo") === "cancelacion" ? "cancelacion" : "cierre"));
  const K = KINDS[kind];
  const [items, setItems] = useState([]);
  const [acts, setActs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);   // null | {id?, ...}
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get("/v1/close-reasons", { params: { include_inactive: true, kind } }),
        api.get("/v1/close-reasons/activity-types"),
      ]);
      setItems(r1.data?.items || []);
      setActs(r2.data?.items || []);
    } catch (e) {
      toast.error(extractApiError(e, "No se pudieron cargar los motivos"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const actName = useMemo(() => Object.fromEntries(acts.map((a) => [a.code, a.name || a.code])), [acts]);

  const save = async () => {
    if (!edit || (edit.label || "").trim().length < 3) { toast.error("Escriba el nombre del motivo (mínimo 3 caracteres)"); return; }
    setSaving(true);
    try {
      const body = { label: edit.label, requires_description: !!edit.requires_description,
        activity_codes: edit.activity_codes || [], active: !!edit.active, sort_order: Number(edit.sort_order) || 100 };
      if (edit.id) await api.put(`/v1/close-reasons/${edit.id}`, body);
      else await api.post("/v1/close-reasons", { ...body, kind });
      toast.success("Motivo guardado");
      setEdit(null);
      load();
    } catch (e) {
      toast.error(extractApiError(e, "No se pudo guardar"));
    } finally { setSaving(false); }
  };

  const toggleAct = (code) => setEdit((cur) => {
    const list = cur.activity_codes || [];
    return { ...cur, activity_codes: list.includes(code) ? list.filter((x) => x !== code) : [...list, code] };
  });

  return (
    <Layout>
      <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <ClipboardList size={20} className="text-slate-700" />
            <div>
              <h1 className="text-lg font-bold text-slate-900">Motivos de cierre y cancelación</h1>
              <p className="text-xs text-slate-500">{K.help}</p>
            </div>
          </div>
          <button onClick={() => setEdit({ ...EMPTY })} data-testid="btn-new-close-reason"
            className="px-3 py-2 rounded-xl text-xs font-bold text-white bg-slate-800 hover:bg-slate-900 flex items-center gap-1.5">
            <Plus size={14} /> Nuevo motivo
          </button>
        </div>

        <div className="flex gap-1.5" data-testid="reason-kind-tabs">
          {Object.entries(KINDS).map(([k, v]) => (
            <button key={k} onClick={() => { setEdit(null); setKind(k); }} data-testid={`reason-kind-${k}`}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold border ${kind === k
                ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
              {v.tab}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Cargando…</div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 p-6 text-sm text-slate-500 text-center">
            {K.empty}
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Motivo</th>
                  <th className="text-left px-3 py-2">Aplica a</th>
                  <th className="text-left px-3 py-2">Descripción</th>
                  <th className="text-left px-3 py-2">Estado</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className={`border-t border-slate-100 ${it.active ? "" : "opacity-50"}`}>
                    <td className="px-3 py-2 font-semibold text-slate-800">{it.label}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">
                      {(it.activity_codes || []).length === 0 ? "Todas las actividades"
                        : it.activity_codes.map((c) => actName[c] || c).join(", ")}
                    </td>
                    <td className="px-3 py-2 text-xs">{kind === "cancelacion" ? "Comentario obligatorio" : it.requires_description ? "Obligatoria" : "Opcional"}</td>
                    <td className="px-3 py-2 text-xs">{it.active ? "Activo" : "Inactivo"}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => setEdit({ ...it })} className="text-xs font-bold text-slate-600 hover:text-slate-900 flex items-center gap-1 ml-auto">
                        <Edit3 size={12} /> Editar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {edit && (
        <div className="fixed inset-0 z-[10000] bg-black/60 flex items-center justify-center p-4" onClick={() => setEdit(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}
               data-testid="close-reason-editor">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <div className="text-sm font-bold text-slate-900">
                {edit.id ? "Editar motivo" : "Nuevo motivo"} · {kind === "cancelacion" ? "cancelación" : "cierre"}
              </div>
              <button onClick={() => setEdit(null)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>
            <div className="px-5 py-4 space-y-3 overflow-y-auto">
              <div>
                <label className="text-[11px] font-bold uppercase text-slate-600">Nombre del motivo</label>
                <input value={edit.label} onChange={(e) => setEdit({ ...edit, label: e.target.value })} maxLength={160}
                  className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-300 text-sm" placeholder={K.placeholder} />
              </div>
              {kind === "cierre" ? (
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!edit.requires_description}
                    onChange={(e) => setEdit({ ...edit, requires_description: e.target.checked })} />
                  Exige descripción (mínimo 10 caracteres)
                </label>
              ) : (
                <p className="text-[11px] text-slate-500">Al cancelar siempre se pide además un comentario (mínimo 10 caracteres).</p>
              )}
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!edit.active} onChange={(e) => setEdit({ ...edit, active: e.target.checked })} />
                Activo
              </label>
              <div>
                <label className="text-[11px] font-bold uppercase text-slate-600">Orden en la lista</label>
                <input type="number" value={edit.sort_order} onChange={(e) => setEdit({ ...edit, sort_order: e.target.value })}
                  className="mt-1 w-28 px-3 py-2 rounded-xl border border-slate-300 text-sm" />
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase text-slate-600">Actividades donde aplica</label>
                <p className="text-[11px] text-slate-500">Sin marcar ninguna = aplica a todas.</p>
                <div className="mt-1 max-h-48 overflow-y-auto rounded-xl border border-slate-200 divide-y divide-slate-100">
                  {acts.map((a) => (
                    <label key={a.code} className="flex items-center gap-2 px-3 py-1.5 text-xs">
                      <input type="checkbox" checked={(edit.activity_codes || []).includes(a.code)} onChange={() => toggleAct(a.code)} />
                      <span className="text-slate-800">{a.name || a.code}</span>
                      <span className="text-slate-400 font-mono">{a.code}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setEdit(null)} className="px-3 py-2 rounded-xl text-xs font-bold border border-slate-300">Cancelar</button>
              <button onClick={save} disabled={saving}
                className="px-4 py-2 rounded-xl text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 flex items-center gap-1.5">
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
